import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { formatDistance } from 'date-fns';
import { EAActivationButton, EAStatusDisplay } from '../components/EAComponents';
import EmergencyControls from '../components/EmergencyControls';
import MT5AccountManager from '../components/MT5AccountManager';
import MT5HistoryIcon from '../components/MT5/MT5HistoryIcon';
import useInactivityTimer from './hooks/useInactivityTimer';
import { INACTIVITY_CONFIG } from './config/inactivityConfig';
import useHeartbeat from './hooks/useHeartbeat';
import config from '../config';

const Dashboard = () => {
  const navigate = useNavigate();
  
  // Initialize inactivity timer for regular users
  useInactivityTimer(INACTIVITY_CONFIG.DEFAULT_TIMEOUT_MINUTES);

  // Keep JWT token alive while the dashboard is open
  useHeartbeat();
  
  const [accountInfo, setAccountInfo] = useState(null);
  const [positions, setPositions] = useState([]);
  const [error, setError] = useState(null);
  const [loading, setLoading] = useState(true);
  const [lastUpdate, setLastUpdate] = useState(null);
  const [eaStatus, setEAStatus] = useState({
    isInstalled: false,
    isActive: false
  });
  const [emergencySettings, setEmergencySettings] = useState({
    is_enabled: true,
    emergency_mode: false
  });
  
  // Add refs to track important state between renders
  const recentAccountSwitchRef = React.useRef(false);
  const currentAccountIdRef = React.useRef(null);
  const eaInstalledAccountsRef = React.useRef(new Set());

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '0.00';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 2,
      maximumFractionDigits: 2
    }).format(num);
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '-';
    return new Intl.NumberFormat('en-US', {
      minimumFractionDigits: 5,
      maximumFractionDigits: 5
    }).format(price);
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'Never';
    const date = new Date(timestamp);
    return formatDistance(date, new Date(), { addSuffix: true });
  };

  const calculateFloatingPL = () => {
    if (accountInfo && accountInfo.floating_pl !== null && accountInfo.floating_pl !== undefined) {
      return accountInfo.floating_pl;
    }
    if (!positions || positions.length === 0) return 0;
    return positions.reduce((total, position) => {
      const profit = parseFloat(position.profit) || 0;
      const swap = parseFloat(position.swap) || 0;
      return total + profit + swap;
    }, 0);
  };

  // Helper to fetch account summary from backend
  const fetchAccountSummary = useCallback(async (selectedAccountId) => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return null; }
      const headers = { 'Authorization': `Bearer ${token}` };
      const url = selectedAccountId
        ? `${config.apiUrl}/account/summary?mt5_account=${encodeURIComponent(selectedAccountId)}`
        : `${config.apiUrl}/account/summary`;
      const res = await fetch(url, { headers });
      if (!res.ok) return null;
      const data = await res.json();
      return data;
    } catch (e) {
      console.warn('Failed to fetch account summary', e);
      return null;
    }
  }, [navigate]);

  // New function to fetch only the EA status
  const fetchEAStatusOnly = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('Fetching EA status from MT5 accounts');
      
      const headers = {
        'Authorization': `Bearer ${token}`,
        'Content-Type': 'application/json'
      };
      
      // Get MT5 accounts with EA status information
      const accountsResponse = await fetch(`${config.apiUrl}/mt5-accounts`, { headers });
      
      if (!accountsResponse.ok) {
        console.error(`Failed to fetch MT5 accounts: ${accountsResponse.status}`);
        return;
      }
      
      const accountsData = await accountsResponse.json();
      console.log('🔍 Dashboard - MT5 accounts with EA status:', accountsData);
      
      // Debug: Check each account's EA status
      if (Array.isArray(accountsData)) {
        accountsData.forEach(account => {
          console.log(`🔍 Dashboard - Account ${account.account_number}:`, {
            ea_installed: account.ea_installed,
            ea_active: account.ea_active,
            is_default: account.is_default
          });
        });
      }
      
      // Find the default account or the first account if no default
      const defaultAccount = accountsData.find(acc => acc.is_default) || accountsData[0];
      
      if (!defaultAccount) {
        console.log('❌ Dashboard - No MT5 account found');
        setEAStatus({
          isInstalled: false,
          isActive: false
        });
        return;
      }
      
      console.log('✅ Dashboard - Using account for EA status:', defaultAccount.account_number);
      console.log('🔍 Dashboard - EA status from API:', {
        installed: defaultAccount.ea_installed,
        active: defaultAccount.ea_active
      });
      
      // Update EA status based on the account data
      const newEAStatus = {
        isInstalled: defaultAccount.ea_installed || false,
        isActive: defaultAccount.ea_active || false
      };
      
      console.log('🔄 Dashboard - Setting EA status to:', newEAStatus);
      setEAStatus(newEAStatus);
      
      // Update the current account reference
      currentAccountIdRef.current = defaultAccount.account_number;
      
    } catch (error) {
      console.error('Error fetching EA status:', error);
      setEAStatus({
        isInstalled: false,
        isActive: false
      });
    }
  }, [navigate]);

  // (Removed duplicate earlier implementations of fetchDataSilently and fetchData in favor of the updated versions below)

  const handleEAActivation = async (mt5Account, mt5Password, mt5Server = 'Swissquote-Server', selectedAccountId = null) => {
    // Define selectedAccount outside the try block so it's accessible in catch block
    let selectedAccount;
    
    try {
      const token = localStorage.getItem('token');
      if (!token) {
        navigate('/login');
        return;
      }

      console.log('Activating EA for account:', mt5Account);
      console.log('Selected Account ID from EAComponents:', selectedAccountId);
      
      // Use the selectedAccountId passed from EAComponents if available
      selectedAccount = selectedAccountId || null;
      
      // Show loading indicator and message to user
      setLoading(true);
      
      // Use the MT5 credentials collected from the dialog
      const requestData = {
        mt5_account: mt5Account,
        mt5_password: mt5Password,
        server: mt5Server,  // Ensure server parameter is included
        mt5_account_id: selectedAccount // Send account ID if available
      };
      
      console.log('EA activation request to API:', {
        url: config.eaActivationUrl || `${config.apiUrl}/ea/activate`, 
        method: 'POST',
        headers: {
          'Authorization': 'Bearer [TOKEN HIDDEN]',
          'Content-Type': 'application/json'
        },
        body: { ...requestData, mt5_password: '********' }  // Hide password in logs
      });
      
      // Variable to store response data, declared outside the try block
      let responseData;
        
      try {
        // Make sure we're using the correct endpoint
        const activationUrl = config.eaActivationUrl || `${config.apiUrl}/ea/activate`;
        console.log(`🔗 Using activation URL: ${activationUrl}`);
        
        const response = await fetch(activationUrl, {
          method: 'POST',
          headers: {
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          },
          body: JSON.stringify(requestData)
        });
        
        console.log('EA activation response status:', response.status, response.statusText);
        console.log('EA activation response headers:', response.headers);

        // Process response
        if (!response.ok) {
          const errorText = await response.text();
          let errorData;
          
          // Try to parse as JSON first
          try {
            errorData = JSON.parse(errorText);
            console.error('VPS server activation error (JSON):', errorData);
          } catch (e) {
            // If not JSON, use raw text
            console.error('VPS server activation error (Text):', errorText);
            errorData = { detail: errorText };
          }
          
          throw new Error(errorData.detail || `Failed to activate EA: ${response.status} ${response.statusText}`);
        }
        
        // Parse response data
        responseData = await response.json();
      } catch (apiError) {
        console.error('API request or response parsing failed:', apiError);
        throw apiError; // Re-throw to be caught by outer catch block
      }
      console.log('EA activation response:', responseData);
      
      // Check for successful response - be more flexible with response format
      const isSuccess = responseData && (
        responseData.status === 'success' || 
        responseData.message?.includes('successfully') ||
        (responseData.vps_response && responseData.vps_response.status === 'success')
      );
      
      if (isSuccess) {
        console.log('✅ EA activation confirmed by VPS server');
        console.log('✅ Full response data:', JSON.stringify(responseData, null, 2));
        
        try {
          // Record that this account now has EA installed
          eaInstalledAccountsRef.current.add(mt5Account);
          console.log('Added account to EA installed list:', mt5Account);
          console.log('Current EA installed accounts:', Array.from(eaInstalledAccountsRef.current));
          
          // Update local state only after confirmed success
          setEAStatus({
            isInstalled: true,
            isActive: true
          });

          // Fetch the latest EA status from the server to ensure consistency
          try {
            await fetchEAStatusOnly();
          } catch (fetchError) {
            console.warn('Warning: Could not refresh EA status, but activation was successful:', fetchError);
          }
          
          // After a short delay, do a full refresh of all dashboard data
          setTimeout(() => {
            try {
              fetchData();
            } catch (refreshError) {
              console.warn('Warning: Could not refresh dashboard, but activation was successful:', refreshError);
            }
          }, 1000);
          
          console.log('🎉 EA activation completed successfully');
          
          // Show success message
          return true; // Return success so the EAActivationButton can show appropriate message
        } catch (uiUpdateError) {
          console.warn('Warning: UI update failed but activation was successful:', uiUpdateError);
          return true; // Still return success since VPS activation worked
        }
      } else {
        console.error('Unexpected response format:', responseData);
        throw new Error(responseData.detail || responseData.message || 'EA activation failed on VPS');
      }

    } catch (error) {
      console.error('❌ EA activation error:', error);
      
      // Enhanced error logging
      console.error('Dashboard EA activation error details:', {
        mt5Account,
        mt5Server,
        selectedAccount,
        timestamp: new Date().toISOString(),
        errorMessage: error.message,
        errorType: error.name,
        stack: error.stack
      });
      
      // Check if this was a post-activation error (VPS succeeded but UI update failed)
      if (error.message && error.message.includes('Warning:')) {
        console.log('🎯 This was a post-activation warning, treating as success');
        return true;
      }
      
      // Don't update status on error - keep current state
      console.log('⚠️ Keeping current EA status due to activation error');
      
      // Re-throw error so EAActivationButton can handle it
      throw error;
    } finally {
      setLoading(false);
    }
  };

  // Update fetchData and fetchDataSilently to also use account summary
  const fetchDataSilently = useCallback(async () => {
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };
      let selectedAccountId = currentAccountIdRef.current;
      if (!selectedAccountId) {
        try {
          const userResponse = await fetch(`${config.apiUrl}/user`, { headers });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            selectedAccountId = userData.mt5_account || userData.default_account;
            if (selectedAccountId) currentAccountIdRef.current = selectedAccountId;
          }
        } catch {}
      }
      if (!selectedAccountId) return;

      // Prefer unified summary
      const summary = await fetchAccountSummary(selectedAccountId);
      if (summary && typeof summary.balance === 'number') {
        setAccountInfo(summary);
      }

      const positionsUrl = `${config.apiUrl}/ea/positions?mt5_account=${selectedAccountId}`;
      const positionsResponse = await fetch(positionsUrl, { headers });
      if (positionsResponse.ok) {
        const positionsData = await positionsResponse.json();
        setPositions(Array.isArray(positionsData) ? positionsData : []);
      }

      const settingsResponse = await fetch(`${config.apiUrl}/settings`, { headers });
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        setEmergencySettings(settingsData);
      }
    } catch (error) {
      console.error('Silent fetch error:', error);
    }
  }, [navigate, fetchAccountSummary]);

  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const token = localStorage.getItem('token');
      if (!token) { navigate('/login'); return; }
      const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

      await fetchEAStatusOnly();
      let selectedAccountId = currentAccountIdRef.current;
      if (!selectedAccountId) {
        try {
          const userResponse = await fetch(`${config.apiUrl}/user`, { headers });
          if (userResponse.ok) {
            const userData = await userResponse.json();
            selectedAccountId = userData.mt5_account || userData.default_account;
            if (selectedAccountId) currentAccountIdRef.current = selectedAccountId;
          }
        } catch {}
      }
      const summary = await fetchAccountSummary(selectedAccountId);
      if (summary) setAccountInfo(summary);

      if (selectedAccountId) {
        const positionsUrl = `${config.apiUrl}/ea/positions?mt5_account=${selectedAccountId}`;
        const positionsResponse = await fetch(positionsUrl, { headers });
        if (positionsResponse.ok) {
          const positionsData = await positionsResponse.json();
          setPositions(Array.isArray(positionsData) ? positionsData : []);
        }
      }

      const settingsResponse = await fetch(`${config.apiUrl}/settings`, { headers });
      if (settingsResponse.ok) {
        const settingsData = await settingsResponse.json();
        setEmergencySettings(settingsData);
      }

      setLastUpdate(new Date().toISOString());
    } catch (err) {
      console.error('Error fetching dashboard data:', err);
      setError(err.message || 'Failed to load data');
    } finally {
      setLoading(false);
    }
  }, [navigate, fetchEAStatusOnly, fetchAccountSummary]);

  // Initialize with dynamic account detection
  useEffect(() => {
    console.log('Initializing dynamic account detection system');
    
    // Don't hardcode any account - let the dynamic system detect the user's account
    // Initialize with basic EA status
    setEAStatus({
      isInstalled: false,
      isActive: false
    });
    
    console.log('Dynamic account system initialized');
  }, []); // Empty dependency array to run only once on mount
  
  // Separate effect to initialize app state and fetch initial data
  useEffect(() => {
    console.log('Data fetch effect running');
    
    // No longer hardcode account 6174074 - let the dynamic system handle account detection
    // Clear any previous account data to ensure fresh detection
    console.log('Clearing hardcoded account data to allow dynamic detection');
    
    // Set default account info for immediate display with zero values
    setAccountInfo({
      balance: 0,
      equity: 0,
      margin: 0,
      free_margin: 0,
      margin_level: 0,
      margin_used: 0,
      currency: 'USD'
    });
    
    // Now fetch real data which will auto-detect the user's account
    const timer = setTimeout(() => {
      console.log('Initial data fetch with dynamic account detection');
      fetchData();
    }, 500);
    
    return () => clearTimeout(timer);
  }, [fetchData]); // Include fetchData in dependencies to fix ESLint warning

  // Define the account switch handler using useCallback for proper dependency management
  const handleAccountSwitch = useCallback((account) => {
    console.log('ACCOUNT SWITCH DETECTED - Triggering full data refresh for account:', account);
    
    // Get account number from the parameter or fallback to DOM or stored ref
    const mt5AccountId = account?.account_number || 
                         account?.id || 
                         document.getElementById('current-mt5-account')?.value || 
                         currentAccountIdRef.current;
    
    console.log('Account switch to ID:', mt5AccountId);
    
    // Clear previous data immediately to avoid showing stale information
    setPositions([]);
    
    // Update the current account ID in all storage mechanisms
    currentAccountIdRef.current = mt5AccountId;
    window.currentAccountId = mt5AccountId;
    localStorage.setItem('last_mt5_account_id', mt5AccountId);
    document.cookie = `current_account_id=${mt5AccountId}; path=/; max-age=86400`;
    
    // Show loading state to indicate data is being fetched
    setLoading(true);
    
    // Clear EA status - it will be refreshed from the MT5 accounts API
    setEAStatus({
      isInstalled: false,
      isActive: false
    });
    
    // Clear account info until we get fresh data
    setAccountInfo({
      balance: 0,
      equity: 0,
      margin: 0,
      free_margin: 0,
      margin_level: 0
    });
    
    // Flag that switching happened
    recentAccountSwitchRef.current = true;
    
    // Force refresh EA status by clearing the accounts cache
    // This ensures we get fresh data for the newly selected account
    console.log('🔄 Forcing fresh EA status fetch for account switch');
    
    // Immediate fetch for the newly selected account with force refresh
    fetchData();
      
    // Reset the flag after data load completes plus a buffer time
    setTimeout(() => {
      recentAccountSwitchRef.current = false;
      console.log('Account switch cooldown period ended, resuming normal updates');
    }, 2000);
    
    // Add page refresh after account switch to ensure consistent data fetching
    setTimeout(() => {
      console.log('Refreshing page after account switch to reset data fetching cycle');
      // Save any additional state to localStorage if needed before refresh
      localStorage.setItem('account_switch_refresh', 'true');
      // Force complete page refresh
      window.location.reload();
    }, 3000); // Refresh after 3 seconds to allow initial data to load
  }, [fetchData]);

  useEffect(() => {
    // Initial data load
    fetchData();
    
    // Define event handlers to handle the account detail from the event
    const handleAccountSwitchedEvent = (event) => {
      console.log('Account switched event detected with detail:', event.detail);
      if (event.detail && event.detail.account) {
        handleAccountSwitch(event.detail.account);
      } else {
        // If no account detail is provided, just call the handler anyway
        handleAccountSwitch();
      }
    };
    
    // Listen for account switches from all possible event sources
    window.addEventListener('mt5AccountSwitched', handleAccountSwitchedEvent);
    document.addEventListener('mt5AccountChanged', handleAccountSwitchedEvent);
    
    // Also watch for clicks on account elements as a fallback
    document.addEventListener('click', (event) => {
      const accountElement = event.target.closest('[data-account-id]');
      if (accountElement) {
        const accountId = accountElement.dataset.accountId;
        const accountNumber = accountElement.dataset.accountNumber;
        console.log(`Account element clicked: ${accountNumber} (${accountId})`);
        handleAccountSwitch({ id: accountId, account_number: accountNumber });
      }
    });
    
    // Set up polling with setInterval for regular updates
    const intervalId = setInterval(() => {
      // Skip silent update if account was switched recently to avoid race conditions
      if (!recentAccountSwitchRef.current) {
        // Use a silent approach for updates
        fetchDataSilently();
      } else {
        console.log('Skipping silent update - account switch in progress');
      }
    }, 5000);
    
    // Clean up interval and event listeners on component unmount
    return () => {
      clearInterval(intervalId);
      window.removeEventListener('mt5AccountSwitched', handleAccountSwitchedEvent);
      document.removeEventListener('mt5AccountChanged', handleAccountSwitchedEvent);
    };
  }, [fetchData, fetchDataSilently, handleAccountSwitch]);

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-500"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center min-h-screen">
        <div className="text-red-600 text-xl mb-4">⚠️ Error: {error}</div>
        <button 
          onClick={() => {
            setError(null);
            fetchData(); // Use the fetchData function instead of reloading the page
          }} 
          className="px-4 py-2 bg-blue-500 text-white rounded hover:bg-blue-600 transition-colors"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto">
      {/* Header Section */}
      <div className="mb-6">
        <div className="flex justify-between items-center mb-4">
          <h1 className="text-2xl font-bold text-gray-900">Trading Dashboard</h1>
          <div className="text-sm text-gray-600">
            Last updated: {formatTimestamp(lastUpdate)}
          </div>
        </div>

        {/* EA Status Cards */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
          <EAStatusDisplay 
            isInstalled={eaStatus.isInstalled}
            isActive={eaStatus.isActive}
          />
          <div className="bg-white rounded-lg shadow p-4">
            <h3 className="text-lg font-semibold mb-4">EA Controls</h3>
            <div className="flex flex-col space-y-2">
              <EAActivationButton 
                isInstalled={eaStatus.isInstalled}
                isActive={eaStatus.isActive}
                onActivate={handleEAActivation}
              />
              {eaStatus.isActive && (
                <div className="w-full bg-gradient-to-r from-indigo-500 via-purple-500 to-pink-500 p-0.5 rounded-lg">
                  <div className="bg-white rounded-lg p-2 flex items-center justify-center space-x-3">
                    <MT5HistoryIcon 
                      account={{
                        login: currentAccountIdRef.current,
                        server: accountInfo?.server || 'Unknown',
                        balance: accountInfo?.balance || 0,
                        equity: accountInfo?.equity || 0
                      }}
                    />
                    <span className="text-sm font-medium text-gray-700">Trade Analytics</span>
                    <MT5HistoryIcon 
                      account={{
                        login: currentAccountIdRef.current,
                        server: accountInfo?.server || 'Unknown',
                        balance: accountInfo?.balance || 0,
                        equity: accountInfo?.equity || 0
                      }}
                    />
                  </div>
                </div>
              )}
            </div>
          </div>
          <EmergencyControls 
            settings={emergencySettings} 
            onUpdate={fetchData} 
          />
        </div>
        
        {/* MT5 Account Management */}
        <div className="mb-8">
          <MT5AccountManager 
            onAccountAdded={fetchData}
            onAccountChanged={handleAccountSwitch} /* Use our specific account switch handler */
          />
        </div>
      </div>

      {/* Account Summary Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Balance</h3>
          <p className="text-3xl font-bold">€{
            accountInfo && (accountInfo.balance !== null && accountInfo.balance !== undefined) ? 
              formatNumber(accountInfo.balance) : 
              formatNumber(0)
          }</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Equity</h3>
          <p className="text-3xl font-bold">€{
            accountInfo && (accountInfo.equity !== null && accountInfo.equity !== undefined) ? 
              formatNumber(accountInfo.equity) : 
              formatNumber(0)
          }</p>
        </div>
        
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-lg font-semibold text-gray-700 mb-2">Floating P/L</h3>
          <p className={`text-3xl font-bold ${
            calculateFloatingPL() >= 0 ? 'text-green-600' : 'text-red-600'
          }`}>
            €{formatNumber(calculateFloatingPL())}
          </p>
        </div>
      </div>

      {/* Positions Table */}
      <div className="bg-white rounded-lg shadow overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h2 className="text-lg font-semibold text-gray-900">
            Active Positions ({positions.length})
          </h2>
        </div>

        <div className="overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                {['Ticket', 'Symbol', 'Type', 'Volume', 'Open Price', 'SL', 'TP', 'Swap', 'Profit'].map((header) => (
                  <th key={header} className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    {header}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {positions.length === 0 ? (
                <tr>
                  <td colSpan="9" className="px-6 py-4 text-center text-gray-500">
                    No active positions
                  </td>
                </tr>
              ) : (
                positions.map((position) => (
                  <tr key={position.ticket}>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {position.ticket}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                      {position.symbol}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <span className={`px-2 py-1 rounded text-xs font-medium ${
                        position.type.toLowerCase() === 'buy'
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}>
                        {position.type}
                      </span>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(position.volume)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPrice(position.open_price)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPrice(position.sl)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatPrice(position.tp)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                      {formatNumber(position.swap)}
                    </td>
                    <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium ${
                      position.profit >= 0 ? 'text-green-600' : 'text-red-600'
                    }`}>
                      {formatNumber(position.profit)}
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );
};

export default Dashboard;